import * as THREE from 'three';
import {isFeatureLine} from '../../mesh';
import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchEllipseObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
    SketchArcObject,
    SketchCircleObject,
    SketchEllipseObject,
    SketchLineObject,
    SketchPointObject,
} from '../../mesh/sketch';
import type {EntitySelectionManagerRuntime} from './EntitySelectionRuntime';
import {
    EntityInteractionEventType,
    type AssemblyReferenceSelectionSnapshotEntry,
    type AssemblyTopologySelectionSnapshotEntry,
} from '../EntitySelectionManager';
import {
    resolveAssemblyReferenceSelectionSnapshotEntry,
    resolveBRepGeometryDescriptor,
    resolveTopologySelectionPick,
} from '../../utils/feature';
import { resolveImportedAssemblyTopologySelectionRef } from '../../imported-model/selection/resolveImportedAssemblyTopologySelectionRef';
import type { SelectionInteractionMeta } from '../../imported-model/render/selection-extension';


/**
 * 清空当前选中的实体快捷引用。
 * @param runtime 运行时上下文。
 */
export function clearCurrentSelectedEntities(runtime: EntitySelectionManagerRuntime): void {
    runtime.currentSelectedPoint = null;
    runtime.currentSelectedLine = null;
    runtime.currentSelectedCircle = null;
    runtime.currentSelectedArc = null;
    runtime.currentSelectedEllipse = null;
    runtime.currentSelectedSpline = null;
    runtime.currentSelectedStyleSpline = null;
    runtime.currentSelectedSlot = null;
}

/**
 * 选中点对象。
 * @param runtime 运行时上下文。
 * @param point 点对象。
 */
export function selectPoint(runtime: EntitySelectionManagerRuntime, point: SketchPointObject): void {
    clearCurrentSelectedEntities(runtime);
    runtime.currentSelectedPoint = point;
}

/**
 * 选中线对象。
 * @param runtime 运行时上下文。
 * @param line 线对象。
 */
export function selectLine(runtime: EntitySelectionManagerRuntime, line: SketchLineObject): void {
    clearCurrentSelectedEntities(runtime);
    runtime.currentSelectedLine = line;
}

/**
 * 选中圆对象。
 * @param runtime 运行时上下文。
 * @param circle 圆对象。
 */
export function selectCircle(runtime: EntitySelectionManagerRuntime, circle: SketchCircleObject): void {
    clearCurrentSelectedEntities(runtime);
    runtime.currentSelectedCircle = circle;
}

/**
 * 选中圆弧对象。
 * @param runtime 运行时上下文。
 * @param arc 圆弧对象。
 */
export function selectArc(runtime: EntitySelectionManagerRuntime, arc: SketchArcObject): void {
    clearCurrentSelectedEntities(runtime);
    runtime.currentSelectedArc = arc;
}

/**
 * 选中椭圆对象。
 * @param runtime 运行时上下文。
 * @param ellipse 椭圆对象。
 */
export function selectEllipse(runtime: EntitySelectionManagerRuntime, ellipse: SketchEllipseObject): void {
    clearCurrentSelectedEntities(runtime);
    runtime.currentSelectedEllipse = ellipse;
}

/**
 * 选中样条对象。
 * @param runtime 运行时上下文。
 * @param spline 样条对象。
 */
export function selectSpline(runtime: EntitySelectionManagerRuntime, spline: THREE.Object3D): void {
    clearCurrentSelectedEntities(runtime);
    runtime.currentSelectedSpline = spline;
}

/**
 * 选中样式样条对象。
 * @param runtime 运行时上下文。
 * @param styleSpline 样式样条对象。
 */
export function selectStyleSpline(runtime: EntitySelectionManagerRuntime, styleSpline: THREE.Object3D): void {
    clearCurrentSelectedEntities(runtime);
    runtime.currentSelectedStyleSpline = styleSpline;
}

/**
 * 选中槽口对象。
 * @param runtime 运行时上下文。
 * @param slot 槽口对象。
 */
export function selectSlot(runtime: EntitySelectionManagerRuntime, slot: THREE.Object3D): void {
    clearCurrentSelectedEntities(runtime);
    runtime.currentSelectedSlot = slot;
}

/**
 * 通过当前选中集合同步快捷引用。
 * @param runtime 运行时上下文。
 * @param selectedPicks 选中命中。
 */
export function syncCurrentSelectedEntities(
    runtime: EntitySelectionManagerRuntime,
    selectedPicks: THREE.Intersection<THREE.Object3D>[]
): void {
    const primaryObject = selectedPicks[0]?.object ?? null;
    if (!primaryObject) {
        clearCurrentSelectedEntities(runtime);
        return;
    }

    if (primaryObject instanceof SketchPointObject) {
        selectPoint(runtime, primaryObject);
        return;
    }
    if (primaryObject instanceof SketchLineObject) {
        selectLine(runtime, primaryObject);
        return;
    }
    if (isSketchCircleObject(primaryObject)) {
        selectCircle(runtime, primaryObject);
        return;
    }
    if (isSketchArcObject(primaryObject)) {
        selectArc(runtime, primaryObject);
        return;
    }
    if (isSketchEllipseObject(primaryObject)) {
        selectEllipse(runtime, primaryObject);
        return;
    }
    if (isSketchStyleSplineObject(primaryObject)) {
        selectStyleSpline(runtime, primaryObject);
        return;
    }
    if (isSketchSplineObject(primaryObject)) {
        selectSpline(runtime, primaryObject);
        return;
    }
    if (isSketchSlotObject(primaryObject)) {
        selectSlot(runtime, primaryObject);
        return;
    }

    clearCurrentSelectedEntities(runtime);
}

/**
 * 判断 hover 是否与当前选中集合冲突。
 * @param runtime 运行时上下文。
 * @param pick 待判断命中。
 * @returns 是否冲突。
 */
export function isHoverMutuallyExclusiveWithSelection(
    runtime: EntitySelectionManagerRuntime,
    pick: THREE.Intersection<THREE.Object3D>
): boolean {
    if (!(pick.object instanceof SketchLineObject) && !isFeatureLine(pick.object)) {
        return false;
    }
    return runtime.selectedPicks.some((selectedPick) => isSamePick(selectedPick, pick));
}

/**
 * 当 hover 与选中冲突时清空 hover。
 * @param runtime 运行时上下文。
 */
export function clearHoverWhenSelectionConflicts(runtime: EntitySelectionManagerRuntime): void {
    if (!runtime.hoveredPick) {
        return;
    }
    if (isHoverMutuallyExclusiveWithSelection(runtime, runtime.hoveredPick)) {
        updateHoverState(runtime, null);
    }
}

/**
 * 更新 hover 状态并派发 enter / leave。
 * @param runtime 运行时上下文。
 * @param nextHoveredPick 新 hover 命中。
 */
export function updateHoverState(
    runtime: EntitySelectionManagerRuntime,
    nextHoveredPick: THREE.Intersection<THREE.Object3D> | null
): void {
    const currentHoveredPick = runtime.hoveredPick;
    if (isSamePick(currentHoveredPick, nextHoveredPick)) {
        /**
         * imported 拓扑拾取会在解析候选时临时挂载面/点代理。
         * 即使最终 hover 对象没有变化，也必须回收本轮未被保留的代理，避免残留覆盖线或隐藏点。
         */
        runtime.syncImportedTopologyProxyRetention?.();
        return;
    }

    if (currentHoveredPick) {
        runtime.publishInteractionEvent(EntityInteractionEventType.EntityHoverLeave, {
            type: EntityInteractionEventType.EntityHoverLeave,
            objectId: currentHoveredPick.object.uuid,
            object: currentHoveredPick.object,
            timestamp: Date.now(),
        });
    }

    runtime.hoveredPick = nextHoveredPick;

    if (runtime.hoveredPick) {
        runtime.publishInteractionEvent(EntityInteractionEventType.EntityHoverEnter, {
            type: EntityInteractionEventType.EntityHoverEnter,
            objectId: runtime.hoveredPick.object.uuid,
            object: runtime.hoveredPick.object,
            timestamp: Date.now(),
        });
    }

    runtime.syncImportedTopologyProxyRetention?.();
}

/**
 * 清空 hover 状态。
 * @param runtime 运行时上下文。
 */
export function clearHoverState(runtime: EntitySelectionManagerRuntime): void {
    updateHoverState(runtime, null);
}

/**
 * 更新选中状态并派发事件。
 * @param runtime 运行时上下文。
 * @param nextSelectedPicks 新的选中命中。
 */
export function updateSelectionState(
    runtime: EntitySelectionManagerRuntime,
    nextSelectedPicks: THREE.Intersection<THREE.Object3D>[],
    interactionMeta?: SelectionInteractionMeta,
): void {
    const uniqueMap = new Map<string, THREE.Intersection<THREE.Object3D>>();
    nextSelectedPicks.forEach((item) => {
        const objectId = item.object.uuid;
        if (!uniqueMap.has(objectId)) {
            uniqueMap.set(objectId, item);
        }
    });

    const uniqueSelectedPicks = Array.from(uniqueMap.values());
    const topologySelectionEntries = uniqueSelectedPicks
        .map((item) => ({
            object: item.object,
            topologyPick: resolveTopologySelectionPick(item.object),
        }))
        .filter((entry): entry is {
            object: THREE.Object3D;
            topologyPick: NonNullable<ReturnType<typeof resolveTopologySelectionPick>>;
        } => entry.topologyPick !== null);
    const topologyPicks = topologySelectionEntries.map((entry) => entry.topologyPick);
    const brepGeometrySnapshotEntries = topologySelectionEntries
        .map((entry) => ({
            ...entry,
            brepGeometryDescriptor: resolveBRepGeometryDescriptor(entry.object),
        }))
        .filter((entry): entry is {
            object: THREE.Object3D;
            topologyPick: NonNullable<ReturnType<typeof resolveTopologySelectionPick>>;
            brepGeometryDescriptor: BRepGeometryDescriptor;
        } => entry.brepGeometryDescriptor !== null);
    const assemblyTopologySelectionEntries = brepGeometrySnapshotEntries
        .map((entry): AssemblyTopologySelectionSnapshotEntry | null => {
            const assemblyTopologyRef = resolveImportedAssemblyTopologySelectionRef(entry.object, entry.topologyPick);
            if (!assemblyTopologyRef) {
                return null;
            }
            return {
                objectId: entry.object.uuid,
                topologyPick: entry.topologyPick,
                assemblyTopologyRef,
                brepGeometryDescriptor: entry.brepGeometryDescriptor,
            };
        })
        .filter((entry): entry is AssemblyTopologySelectionSnapshotEntry => entry !== null);
    const assemblyTopologyRefs = assemblyTopologySelectionEntries
        .map((entry) => entry.assemblyTopologyRef);
    const brepGeometryDescriptors = brepGeometrySnapshotEntries
        .map((entry) => entry.brepGeometryDescriptor);
    const assemblyReferenceSelectionEntries = uniqueSelectedPicks
        .map((item) => resolveAssemblyReferenceSelectionSnapshotEntry(item.object, runtime.renderer))
        .filter((entry): entry is AssemblyReferenceSelectionSnapshotEntry => entry !== null);
    const assemblyReferenceSelectionRefs = assemblyReferenceSelectionEntries
        .map((entry) => entry.referenceRef);
    const previous = runtime.selectedPicks;
    const nextInteractionMeta = uniqueSelectedPicks.length > 0 ? interactionMeta : undefined;
    const interactionMetaChanged = !areSelectionInteractionMetasEqual(
        runtime.currentSelectionInteractionMeta,
        nextInteractionMeta,
    );
    const changed = !isPickArrayEqual(previous, uniqueSelectedPicks) || interactionMetaChanged;
    runtime.selectedPicks = uniqueSelectedPicks;
    runtime.currentSelectionInteractionMeta = nextInteractionMeta;

    clearHoverWhenSelectionConflicts(runtime);
    syncCurrentSelectedEntities(runtime, uniqueSelectedPicks);
    publishCurrentSelectionEvent(runtime);

    /**
     * 说明：
     * 1. 宿主层需要一个“当前选择快照已经完成同步”的统一事件，
     *    不能继续依赖 `SelectionCleared + EntitySelected` 两条事件自行拼装；
     * 2. 这里在 `selectedPicks` 与快捷引用都完成同步之后再统一发布，
     *    保证上层拿到的一定是稳定快照；
     * 3. 即使本次选择与上次完全相同，也允许宿主重新对齐状态，避免 UI 吸收链出现一次性漏同步后无法自愈。
     */
    runtime.publishInteractionEvent(EntityInteractionEventType.SelectionUpdated, {
        type: EntityInteractionEventType.SelectionUpdated,
        objectId: uniqueSelectedPicks[0]?.object.uuid ?? null,
        object: uniqueSelectedPicks[0]?.object ?? null,
        selectionSourceObjectId: uniqueSelectedPicks[0]?.object.uuid ?? null,
        selectionSourceObject: uniqueSelectedPicks[0]?.object ?? null,
        selectedObjectIds: uniqueSelectedPicks.map((item) => item.object.uuid),
        selectedObjects: uniqueSelectedPicks.map((item) => item.object),
        selectionSourceObjectIds: uniqueSelectedPicks.map((item) => item.object.uuid),
        selectionSourceObjects: uniqueSelectedPicks.map((item) => item.object),
        topologyPick: topologyPicks[0] ?? null,
        topologyPicks,
        assemblyTopologyRef: assemblyTopologyRefs[0] ?? null,
        assemblyTopologyRefs,
        assemblyTopologySelectionEntries,
        assemblyReferenceSelectionRef: assemblyReferenceSelectionRefs[0] ?? null,
        assemblyReferenceSelectionRefs,
        assemblyReferenceSelectionEntries,
        brepGeometryDescriptor: brepGeometryDescriptors[0] ?? null,
        brepGeometryDescriptors,
        interactionMeta: runtime.currentSelectionInteractionMeta,
        timestamp: Date.now(),
    });

    runtime.syncImportedTopologyProxyRetention?.();

    if (!changed) {
        return;
    }

    if (shouldSuppressOrdinaryEntitySelectedForCommandTopology(runtime)) {
        publishEmptyEntitySelectedEvent(runtime);
        return;
    }

    runtime.publishInteractionEvent(EntityInteractionEventType.EntitySelected, {
        type: EntityInteractionEventType.EntitySelected,
        objectId: uniqueSelectedPicks[0]?.object.uuid ?? null,
        object: uniqueSelectedPicks[0]?.object ?? null,
        selectedObjectIds: uniqueSelectedPicks.map((item) => item.object.uuid),
        selectedObjects: uniqueSelectedPicks.map((item) => item.object),
        interactionMeta: runtime.currentSelectionInteractionMeta,
        timestamp: Date.now(),
    });

}

/**
 * 判断当前普通 EntitySelected 是否应被命令态拓扑高亮接管。
 *
 * 设计说明：
 * 1. Mate 命令在 `SelectionUpdated` 阶段已经保存完整候选集合；
 * 2. 继续发布最后一次点击的普通 selected 会让通用材质状态机只高亮最后一个面；
 * 3. 因此命令态存在时仍保留 `SelectionUpdated`，但把普通 selected 降级为空选择。
 *
 * @param runtime 选择运行时上下文。
 * @returns 是否应阻止普通 selected 继续表达最后一次点击对象。
 */
function shouldSuppressOrdinaryEntitySelectedForCommandTopology(
    runtime: EntitySelectionManagerRuntime,
): boolean {
    return (runtime.commandSelectedTopologyProxyObjects?.length ?? 0) > 0;
}

/**
 * 发布空普通选中事件，让通用交互状态机清理 selected 材质。
 *
 * @param runtime 选择运行时上下文。
 */
function publishEmptyEntitySelectedEvent(runtime: EntitySelectionManagerRuntime): void {
    runtime.publishInteractionEvent(EntityInteractionEventType.EntitySelected, {
        type: EntityInteractionEventType.EntitySelected,
        objectId: null,
        object: null,
        selectedObjectIds: [],
        selectedObjects: [],
        timestamp: Date.now(),
    });
}

/**
 * 根据实体对象数组派发选中结果。
 * @param runtime 运行时上下文。
 */
export function publishCurrentSelectionEvent(runtime: EntitySelectionManagerRuntime): void {
    runtime.publishInteractionEvent(EntityInteractionEventType.SelectionCleared, {
        type: EntityInteractionEventType.SelectionCleared,
        timestamp: Date.now(),
    });

    if (runtime.currentSelectedPoint) {
        runtime.publishInteractionEvent(EntityInteractionEventType.PointSelected, {
            type: EntityInteractionEventType.PointSelected,
            objectId: runtime.currentSelectedPoint.uuid,
            object: runtime.currentSelectedPoint,
            timestamp: Date.now(),
        });
        return;
    }
    if (runtime.currentSelectedLine) {
        runtime.publishInteractionEvent(EntityInteractionEventType.LineSelected, {
            type: EntityInteractionEventType.LineSelected,
            objectId: runtime.currentSelectedLine.uuid,
            object: runtime.currentSelectedLine,
            timestamp: Date.now(),
        });
        return;
    }
    if (runtime.currentSelectedCircle) {
        runtime.publishInteractionEvent(EntityInteractionEventType.CircleSelected, {
            type: EntityInteractionEventType.CircleSelected,
            objectId: runtime.currentSelectedCircle.uuid,
            object: runtime.currentSelectedCircle,
            timestamp: Date.now(),
        });
        return;
    }
    if (runtime.currentSelectedArc) {
        runtime.publishInteractionEvent(EntityInteractionEventType.ArcSelected, {
            type: EntityInteractionEventType.ArcSelected,
            objectId: runtime.currentSelectedArc.uuid,
            object: runtime.currentSelectedArc,
            timestamp: Date.now(),
        });
        return;
    }
    if (runtime.currentSelectedEllipse) {
        runtime.publishInteractionEvent(EntityInteractionEventType.EllipseSelected, {
            type: EntityInteractionEventType.EllipseSelected,
            objectId: runtime.currentSelectedEllipse.uuid,
            object: runtime.currentSelectedEllipse,
            timestamp: Date.now(),
        });
        return;
    }
    if (runtime.currentSelectedStyleSpline) {
        runtime.publishInteractionEvent(EntityInteractionEventType.StyleSplineSelected, {
            type: EntityInteractionEventType.StyleSplineSelected,
            objectId: runtime.currentSelectedStyleSpline.uuid,
            object: runtime.currentSelectedStyleSpline,
            timestamp: Date.now(),
        });
        return;
    }
    if (runtime.currentSelectedSpline) {
        runtime.publishInteractionEvent(EntityInteractionEventType.SplineSelected, {
            type: EntityInteractionEventType.SplineSelected,
            objectId: runtime.currentSelectedSpline.uuid,
            object: runtime.currentSelectedSpline,
            timestamp: Date.now(),
        });
        return;
    }
    if (runtime.currentSelectedSlot) {
        runtime.publishInteractionEvent(EntityInteractionEventType.SlotSelected, {
            type: EntityInteractionEventType.SlotSelected,
            objectId: runtime.currentSelectedSlot.uuid,
            object: runtime.currentSelectedSlot,
            timestamp: Date.now(),
        });
    }
}

/**
 * 判断两个命中是否一致。
 * @param left 左命中。
 * @param right 右命中。
 * @returns 是否一致。
 */
export function isSamePick(
    left: THREE.Intersection<THREE.Object3D> | null,
    right: THREE.Intersection<THREE.Object3D> | null
): boolean {
    if (left === right) {
        return true;
    }
    if (!left || !right) {
        return false;
    }
    return left.object.uuid === right.object.uuid;
}

/**
 * 判断两个命中数组是否一致。
 * @param left 左数组。
 * @param right 右数组。
 * @returns 是否一致。
 */
export function isPickArrayEqual(
    left: THREE.Intersection<THREE.Object3D>[],
    right: THREE.Intersection<THREE.Object3D>[]
): boolean {
    if (left.length !== right.length) {
        return false;
    }
    for (let index = 0; index < left.length; index += 1) {
        if (!isSamePick(left[index], right[index])) {
            return false;
        }
    }
    return true;
}

/**
 * 规范化 entityHighlightTargets，用于稳定比较。
 *
 * @param targets 原始高亮目标列表。
 * @returns 规范化后的高亮目标列表。
 */
function normalizeEntityHighlightTargets(
    targets: SelectionInteractionMeta['entityHighlightTargets'],
): Array<{ objectId: string; primitiveIndices?: number[] }> {
    if (!Array.isArray(targets)) {
        return [];
    }

    return targets.map((target) => ({
        objectId: target.objectId,
        primitiveIndices: Array.isArray(target.primitiveIndices)
            ? [...target.primitiveIndices].sort((left, right) => left - right)
            : undefined,
    }));
}

/**
 * 比较两份选择交互元信息是否等价。
 *
 * 设计说明：
 * 1. imported-model 多个实体节点会共享同一个 selectionObjectId，只靠 entityHighlightTargets 区分；
 * 2. 选择层必须把 interactionMeta 变化视为“选中变化”，否则 InteractionStateManager 收不到 EntitySelected；
 * 3. 这里只比较会影响设计树 imported-model 视觉路由的字段，避免无关字段阻断刷新。
 *
 * @param left 左侧元信息。
 * @param right 右侧元信息。
 * @returns 等价时返回 `true`。
 */
export function areSelectionInteractionMetasEqual(
    left: SelectionInteractionMeta | undefined,
    right: SelectionInteractionMeta | undefined,
): boolean {
    if (left === right) {
        return true;
    }
    if (!left || !right) {
        return !left && !right;
    }

    if (left.source !== right.source || left.semantic !== right.semantic) {
        return false;
    }
    if (left.assemblyNodeId !== right.assemblyNodeId || left.projectNodeKey !== right.projectNodeKey) {
        return false;
    }
    if (left.glbNodeRole !== right.glbNodeRole) {
        return false;
    }
    if (left.bodyBindingId !== right.bodyBindingId) {
        return false;
    }

    // 普通场景 Hover 没有显式 primitive 集合，不为相同空语义构建数组和 JSON 字符串。
    if (!left.entityHighlightTargets && !right.entityHighlightTargets) return true;
    const leftTargets = normalizeEntityHighlightTargets(left.entityHighlightTargets);
    const rightTargets = normalizeEntityHighlightTargets(right.entityHighlightTargets);
    return JSON.stringify(leftTargets) === JSON.stringify(rightTargets);
}

